// The Meta Pixel's deterministic event ids and advanced-matching hashes
// (#780), written once so that the browser and the Conversions API (#781,
// functions/billing/metaCapi.js) make the same ones: Meta removes a server
// event whose `eventID` matches a browser one, so the two must agree to the
// character. Here rather than in marketing/metaPixel.js because this directory
// is what the deploy mirror copies into functions/domain/, and the marketing
// site sends neither event.
//
// The hash is SHA-256 by WebCrypto (`globalThis.crypto.subtle`), which the
// browser has in a secure context and Node has natively (functions run Node
// 22). Not in the Rust core: it is a primitive both hosts already ship, not a
// rule three platforms must agree on, and the phones send no Meta events.
//
// - StartTrial: `starttrial_<sha256 hex of the uid>` — the same hash that goes
//   as `external_id`. The raw uid never reaches Meta, in an id or anywhere.
// - AddPaymentInfo: `addpaymentinfo_<Checkout session id>`. Stripe's session
//   id is opaque, names nobody, and the server's webhook sees the same one.
// - `em`: the SHA-256 hex of the trimmed, lowercased email.

const HEX = /^[0-9a-f]{64}$/;

const defaultSubtle = () => globalThis.crypto?.subtle ?? null;

/** The SHA-256 of `text` as lowercase hex, or null where it cannot be hashed. */
export async function sha256Hex(text, subtle = defaultSubtle()) {
  try {
    if (!subtle || typeof TextEncoder === 'undefined') return null;
    const digest = await subtle.digest('SHA-256', new TextEncoder().encode(String(text)));
    const hex = Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('');
    return HEX.test(hex) ? hex : null;
  } catch {
    return null;
  }
}

/** An email as Meta normalises it before hashing: trimmed and lowercased, or null. */
export function normalisedEmail(email) {
  const e = typeof email === 'string' ? email.trim().toLowerCase() : '';
  return e.includes('@') ? e : null;
}

/** The uid as Meta receives it, as `external_id` and inside StartTrial's id: hashed, or null. */
export async function hashedUid(uid, subtle = defaultSubtle()) {
  return typeof uid === 'string' && uid ? sha256Hex(uid, subtle) : null;
}

/**
 * StartTrial's deterministic id for the account `uid`:
 * `starttrial_<sha256 hex of the uid>`, or null where it cannot be hashed —
 * never the raw uid in its place.
 */
export async function startTrialEventId(uid, subtle = defaultSubtle()) {
  const hash = await hashedUid(uid, subtle);
  return hash ? `starttrial_${hash}` : null;
}

/** AddPaymentInfo's deterministic id for a completed Checkout session. */
export const addPaymentInfoEventId = (sessionId) => `addpaymentinfo_${sessionId}`;

/**
 * The advanced-matching fields for a sign-up: `em` and `external_id`, each a
 * SHA-256 hex, and only the ones that could be made. Never the plain values.
 */
export async function hashedMatching({ email, uid } = {}, subtle = defaultSubtle()) {
  const out = {};
  const e = normalisedEmail(email);
  const em = e ? await sha256Hex(e, subtle) : null;
  if (em) out.em = em;
  const external = await hashedUid(uid, subtle);
  if (external) out.external_id = external;
  return out;
}
